import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api, { errMsg, openFile } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { AU_STATES, PROPERTY_OPTIONS, ACTIVITY_TYPES, QUOTE_TYPES, INTERESTED_SOLUTIONS, LEAD_PRIORITIES, MANAGER_ROLES, currency, fmtDate } from "../lib/constants";
import AddressAutocomplete from "../components/AddressAutocomplete";
import TaxInvoicePanel from "../components/TaxInvoicePanel";
import PrepostPanel from "../components/PrepostPanel";
import { LeadChat } from "../components/LeadChat";
import { ShareLeadDialog } from "../components/ShareLeadDialog";
import ActivityHub from "../components/ActivityHub";
import { LostReasonModal } from "../components/LostReasonModal";
import { ActionsMenu } from "../components/ActionsMenu";
import { QuotePdfActions } from "../components/QuotePdfActions";
import { melbourneInput } from "../lib/melbourneTime";
import { ConfirmAction } from "../components/ConfirmAction";
import { SignedProposalArchive } from "../components/SignedProposalArchive";
import { ProposalPdfArchive } from "../components/ProposalPdfArchive";
import * as Icons from "lucide-react";
import { toast } from "sonner";

const TABS = ["Overview", "Property Details", "Activity", "Documents", "Chat", "Quotes", "Prepost", "Tax Invoice"];

const DOC_LABELS = ["Power bill", "Switchboard photo", "Meter photo", "Battery location 1", "Battery location 2", "Battery location 3", "Roof layout", "Custom"];

const Sel = ({ label, value, opts, onChange, testid }) => (
  <div><label className="label-caps">{label}</label>
    <select value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background">
      <option value="">— Select —</option>{opts.map((o) => <option key={o}>{o}</option>)}</select></div>
);

const StatusPill = ({ ok, label }) => (
  <span className={`text-xs font-semibold px-3 py-1 rounded-full inline-flex items-center gap-1 ${ok ? "bg-emerald-100 text-emerald-700" : "bg-muted text-muted-foreground"}`}>
    {ok ? <Icons.Check size={12} /> : <Icons.Circle size={12} />} {label}
  </span>
);

// In merged (single-page) view each tab becomes a collapsible card; in tabbed view children render as-is.
const Section = ({ merged, name, icon, open, toggle, children }) => {
  if (!merged) return children;
  const Icon = Icons[icon] || Icons.ChevronDown;
  return (
    <div className="bg-card border border-border rounded-md shadow-sm overflow-hidden" data-testid={`section-${name}`}>
      <button type="button" onClick={() => toggle(name)} data-testid={`section-toggle-${name}`}
        className="w-full flex items-center justify-between px-5 py-3.5 text-left hover:bg-muted/40 transition-colors">
        <span className="font-head font-semibold flex items-center gap-2"><Icon size={17} className="text-secondary" /> {name}</span>
        <Icons.ChevronDown size={18} className={`transition-transform duration-200 ${open ? "rotate-180" : ""} text-muted-foreground`} />
      </button>
      {open && <div className="px-5 pb-5 border-t border-border/60 pt-4">{children}</div>}
    </div>
  );
};

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, setUiPref } = useAuth();
  const [lead, setLead] = useState(null);
  const [shareDocument, setShareDocument] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [users, setUsers] = useState([]);
  const canAssign = MANAGER_ROLES.includes(user?.role);
  const [tab, setTab] = useState(() => TABS.includes(searchParams.get("tab")) ? searchParams.get("tab") : "Overview");
  useEffect(() => { const requested = searchParams.get("tab"); if (TABS.includes(requested)) setTab(requested); }, [searchParams]);
  const merged = user?.ui_prefs?.lead_view === "merged";
  const [collapsed, setCollapsed] = useState({});
  const isOpen = (name) => !collapsed[name];
  const toggleSection = (name) => setCollapsed((c) => ({ ...c, [name]: !c[name] }));
  const show = (name) => merged || tab === name;
  const [stages, setStages] = useState([]);
  const [activities, setActivities] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [docs, setDocs] = useState([]);
  const [prop, setProp] = useState({});
  const [note, setNote] = useState("");
  const [cbShow, setCbShow] = useState(false);
  const [cb, setCb] = useState({ title: "Call back customer", due_at: "" });
  const [comms, setComms] = useState([]);
  const [calling, setCalling] = useState(false);
  const [commsCfg, setCommsCfg] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [fin, setFin] = useState(null);
  const [bookingLink, setBookingLink] = useState("");
  const [dups, setDups] = useState([]);
  const canDeleteDoc = MANAGER_ROLES.includes(user?.role);
  const [docLabel, setDocLabel] = useState("Power bill");
  const [docCustom, setDocCustom] = useState("");
  const [docFile, setDocFile] = useState([]);
  const [docUploading, setDocUploading] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [editLabel, setEditLabel] = useState("");
  const [editCustom, setEditCustom] = useState("");
  const [lostOpen, setLostOpen] = useState(false);
  const isSuper = user?.role === "super_admin";
  const locked = !!lead?.locked;
  const canEdit = !locked || isSuper;

  const reloadDocs = () => api.get(`/leads/${id}/documents`).then((r) => setDocs(r.data)).catch(() => {});

  const uploadDoc = async () => {
    if (!docFile.length) { toast.error("Please choose at least one file"); return; }
    const label = docLabel === "Custom" ? docCustom.trim() : docLabel;
    if (docLabel === "Custom" && !label) { toast.error("Please enter a custom label"); return; }
    setDocUploading(true);
    let ok = 0; const failed = [];
    for (const f of docFile) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("label", label);
        await api.post(`/leads/${id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        ok += 1;
      } catch (e) { failed.push(`${f.name}: ${errMsg(e)}`); }
    }
    if (ok) toast.success(`${ok} document${ok > 1 ? "s" : ""} uploaded`);
    if (failed.length) toast.error(failed.join(" · "), { duration: 8000 });
    if (ok) { setDocFile([]); setDocCustom(""); setDocLabel("Power bill"); reloadDocs(); }
    setDocUploading(false);
  };

  const startEditDoc = (d) => {
    setEditingDoc(d.id);
    const known = DOC_LABELS.includes(d.label);
    setEditLabel(d.label && known ? d.label : (d.label ? "Custom" : "Power bill"));
    setEditCustom(d.label && !known ? d.label : "");
  };

  const saveEditDoc = async (docId) => {
    const label = editLabel === "Custom" ? editCustom.trim() : editLabel;
    if (editLabel === "Custom" && !label) { toast.error("Please enter a custom label"); return; }
    try {
      await api.patch(`/documents/${docId}`, { label });
      toast.success("Label updated");
      setEditingDoc(null);
      reloadDocs();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const deleteDoc = async (docId) => {
      await api.delete(`/documents/${docId}`);
      toast.success("Document deleted");
      reloadDocs();
  };

  const deleteLead = async () => {
      await api.delete(`/leads/${id}`);
      toast.success("Lead moved to Recycle Bin");
      navigate("/app/leads");
  };

  const loadFin = () => api.get(`/leads/${id}/finalization`).then((r) => setFin(r.data)).catch(() => {});

  const load = () => api.get(`/leads/${id}`).then((r) => { setLead(r.data); setProp(r.data.property_details || {}); }).catch((e) => { if (e.response?.status === 404) setNotFound(true); });
  const loadAct = () => api.get(`/leads/${id}/activities`).then((r) => setActivities(r.data)).catch(() => {});
  const loadComms = () => api.get(`/leads/${id}/communications`).then((r) => setComms(r.data)).catch(() => {});
  useEffect(() => {
    load(); loadAct(); loadComms();
    api.get("/config/stages").then((r) => setStages(r.data));
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get(`/leads/${id}/quotes`).then((r) => setQuotes(r.data)).catch(() => {});
    api.get(`/leads/${id}/documents`).then((r) => setDocs(r.data)).catch(() => {});
    api.get("/comms/config").then((r) => setCommsCfg(r.data)).catch(() => {});
    api.get(`/leads/${id}/duplicates`).then((r) => setDups(r.data.duplicates || [])).catch(() => {});
    loadFin();
  }, [id]);

  // live-refresh the timeline while viewing the Activity tab
  useEffect(() => {
    if (tab !== "Activity") return;
    loadComms(); loadAct();
    const t = setInterval(() => { loadComms(); loadAct(); }, 12000);
    return () => clearInterval(t);
  }, [tab, id]);

  if (notFound) return (
    <div className="text-center py-20" data-testid="lead-not-found">
      <Icons.ShieldAlert size={40} className="mx-auto text-muted-foreground mb-3" />
      <h2 className="text-xl font-head font-bold">Lead not found</h2>
      <p className="text-muted-foreground mt-1">This lead doesn&apos;t exist or you don&apos;t have access to it.</p>
      <button data-testid="lead-not-found-back" onClick={() => navigate("/app/leads")} className="mt-4 px-5 py-2 rounded-md bg-primary text-primary-foreground font-semibold">Back to leads</button>
    </div>
  );
  if (!lead) return <div className="text-muted-foreground">Loading…</div>;

  const stageObj = stages.find((s) => s.id === lead.stage);
  const setField = (k, v) => setLead({ ...lead, [k]: v });
  const userName = (uid) => users.find((u) => u.id === uid)?.name || (uid ? "—" : "Unassigned");
  const assign = async (uid) => {
    try {
      const { data } = await api.put(`/leads/${id}`, { assigned_to: uid || null });
      setLead(data); loadAct(); toast.success(uid ? `Assigned to ${userName(uid)}` : "Unassigned");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const saveLead = async () => {
    const full_address = [lead.street, lead.suburb, lead.state, lead.postcode].filter(Boolean).join(", ");
    try {
      const { data } = await api.put(`/leads/${id}`, { ...lead, full_address });
      setLead(data); setProp(data.property_details || {});
      toast.success("Saved"); loadAct();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const changeStage = async (stage, extra = {}) => {
    if (stage === "lost" && !extra.lost_reason) { setLostOpen(true); return; }
    try {
      const { data } = await api.put(`/leads/${id}`, { stage, substage: "", ...extra });
      setLead(data); loadAct(); toast.success("Stage updated");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const toggleLock = async () => {
    try { await api.post(`/leads/${id}/lock`, { locked: !locked }); toast.success(locked ? "Project unlocked" : "Project locked"); load(); loadAct(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const duplicateQuote = async (q) => {
    try { const { data } = await api.post(`/quotes/${q.id}/duplicate`); toast.success(`Duplicated as ${data.quote_no}`); navigate(`/app/quotes/${data.id}`); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const changeSub = async (substage) => { const { data } = await api.put(`/leads/${id}`, { substage }); setLead(data); };
  const saveProp = async () => { try { await api.put(`/leads/${id}`, { property_details: prop }); toast.success("Property details saved"); loadFin(); } catch (e) { toast.error(errMsg(e)); } };

  const submitFin = async () => {
    try { await api.post(`/leads/${id}/finalization/submit`); toast.success("Submitted for approval"); load(); loadAct(); loadFin(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const approveFin = async () => {
    try { await api.post(`/leads/${id}/finalization/approve`); toast.success("Approved & finalized"); load(); loadAct(); loadFin(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const rejectFin = async () => {
    const reason = window.prompt("Reason for sending this sale back to the consultant:");
    if (reason === null) return;
    try { await api.post(`/leads/${id}/finalization/reject`, { reason }); toast.success("Sent back to consultant"); load(); loadAct(); loadFin(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const addActivity = async (type, msg) => {
    await api.post("/activities", { lead_id: id, type, message: msg || "" });
    if (type === "note") setNote(""); loadAct(); toast.success("Activity logged");
  };
  const createCallback = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/tasks", { lead_id: id, title: cb.title, due_at: cb.due_at, description: `Callback for ${lead.uid}` });
      await addActivity("note", `Callback scheduled for ${fmtDate(data.due_at)} Melbourne`);
      setCbShow(false); toast.success("Callback task added to calendar");
    } catch (e) { toast.error(errMsg(e)); }
  };


  const startCall = async () => {
    if (!lead.phone) return;
    setCalling(true);
    // Normalise to the AU dialling format Uniden/VOCPhone validates against: "61" prefix, no "+"
    let d = (lead.phone || "").replace(/\D/g, "");
    let dial;
    if (d.startsWith("0061")) dial = d.slice(2);      // 0061... -> 61...
    else if (d.startsWith("61")) dial = d;            // already 61...
    else if (d.startsWith("0")) dial = "61" + d.slice(1); // 04.. -> 614..
    else if (d.length === 9) dial = "61" + d;          // 4........ -> 614........
    else dial = d;
    // hand off to the rep's device dialer / registered VOCPhone SIP softphone
    window.location.href = `tel:${dial}`;
    try {
      await api.post(`/leads/${id}/call`, { via: "tel" });
      loadComms(); loadAct();
      toast.success("Opening your dialer / VOCPhone softphone…");
    } catch (e) { toast.error(errMsg(e, "Failed to start call")); }
    finally { setCalling(false); }
  };

  return (
    <div className="space-y-6">
      <button onClick={() => navigate("/app/leads")} className="text-sm text-muted-foreground flex items-center gap-1"><Icons.ChevronLeft size={16} /> Back to leads</button>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 max-w-full">
          <div className="flex flex-wrap items-center gap-3">
            <h1 data-testid="lead-title" className="max-w-full [overflow-wrap:anywhere] text-3xl font-head font-extrabold">{lead.first_name} {lead.last_name}</h1>
            <span data-testid="lead-reference" className="text-sm font-semibold px-2 py-0.5 rounded bg-primary/10 text-primary">{lead.uid}</span>
          </div>
          <p data-testid="lead-address" className="[overflow-wrap:anywhere] text-muted-foreground">{lead.full_address || "No address"}</p>
        </div>
        <div className="flex gap-2 items-end flex-wrap">
          <div><label className="label-caps">Stage</label>
            <select value={lead.stage} onChange={(e) => changeStage(e.target.value)} data-testid="stage-select"
              className="block mt-1 px-3 py-2 rounded-md border border-input bg-card font-semibold">
              {stages.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select></div>
          {stageObj?.substages?.length > 0 && (
            <div><label className="label-caps">Sub-stage</label>
              <select value={lead.substage || ""} onChange={(e) => changeSub(e.target.value)} data-testid="substage-select"
                className="block mt-1 px-3 py-2 rounded-md border border-input bg-card">
                <option value="">— Select —</option>{stageObj.substages.map((s) => <option key={s}>{s}</option>)}</select></div>
          )}
          <ActionsMenu id="lead-actions" items={[
            { id: "lead-follow-updates", icon: "Bell", label: (lead.follower_ids || []).includes(user.id) ? "Unfollow lead updates" : "Follow lead updates", onSelect: async () => {
              try { const following = !(lead.follower_ids || []).includes(user.id); await api.put(`/leads/${id}/follow`, { following }); setLead((old) => ({ ...old, follower_ids: following ? [...(old.follower_ids || []), user.id] : (old.follower_ids || []).filter((value) => value !== user.id) })); toast.success(following ? "Following lead updates" : "Unfollowed lead updates"); } catch (e) { toast.error(errMsg(e)); }
            } },
            { id: "callback-btn", icon: "CalendarClock", label: "Schedule callback", onSelect: () => setCbShow(true) },
            { id: "send-to-prepost-btn", icon: "ClipboardCheck", label: "Send to Prepost", hidden: !MANAGER_ROLES.includes(user?.role),
              onSelect: async () => { try { await api.post(`/leads/${id}/send-to-prepost`); toast.success("Sent to Prepost queue"); } catch (e) { toast.error(errMsg(e)); } } },
            { id: "lock-toggle-btn", icon: locked ? "Unlock" : "Lock", label: locked ? "Unlock project" : "Lock project", hidden: !isSuper, onSelect: toggleLock },
            { id: "delete-lead-btn", icon: "Trash2", label: "Move to Recycle Bin", hidden: !isSuper, destructive: true,
              onSelect: () => setConfirm({ title: `Delete ${lead.first_name} ${lead.last_name}?`, description: "This lead will move to the Recycle Bin and can be restored for 30 days.", label: "Move to Recycle Bin", run: deleteLead }) },
          ]} />
        </div>
      </div>

      {locked && (
        <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-md p-3 text-sm flex items-center gap-2" data-testid="locked-banner">
          <Icons.Lock size={16} className="shrink-0" />
          <span><b>Project locked</b> — the customer has signed. {isSuper ? "As Super Admin your edits are allowed and recorded in the activity log." : "Customer, property, quote and pricing details can no longer be changed. Ask a Super Admin to unlock if needed."}</span>
        </div>
      )}
      {lostOpen && <LostReasonModal lead={lead} onClose={() => setLostOpen(false)} onConfirm={(reason, notes) => { setLostOpen(false); changeStage("lost", { lost_reason: reason, lost_notes: notes }); }} />}

      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border">
        <div className="flex flex-wrap gap-1">
          {!merged && TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t}`}
              className={`px-4 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition-all ${tab === t ? "border-primary text-primary" : "border-transparent text-muted-foreground"}`}>{t}</button>
          ))}
        </div>
        <div className="flex items-center gap-1 pb-1 ml-auto">
          <span className="text-xs text-muted-foreground mr-1 hidden sm:inline">View</span>
          <button onClick={() => setUiPref("lead_view", "tabbed")} data-testid="leadview-tabbed"
            className={`px-2.5 py-1 rounded-md text-xs font-semibold flex items-center gap-1 transition-colors ${!merged ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}><Icons.LayoutList size={13} /> Tabs</button>
          <button onClick={() => setUiPref("lead_view", "merged")} data-testid="leadview-merged"
            className={`px-2.5 py-1 rounded-md text-xs font-semibold flex items-center gap-1 transition-colors ${merged ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}><Icons.Rows3 size={13} /> Single page</button>
        </div>
      </div>
      {merged && (
        <div className="flex gap-3 text-xs -mt-2">
          <button onClick={() => setCollapsed({})} data-testid="section-expand-all" className="text-primary font-medium hover:underline">Expand all</button>
          <button onClick={() => setCollapsed(Object.fromEntries(TABS.map((t) => [t, true])))} data-testid="section-collapse-all" className="text-muted-foreground hover:underline">Collapse all</button>
        </div>
      )}

      <div className={merged ? "lg:grid lg:grid-cols-3 lg:gap-4 lg:items-start" : "contents"}>
      <div className={merged ? "lg:col-span-2 space-y-4" : "contents"}>

      {show("Overview") && (<Section merged={merged} name="Overview" icon="LayoutList" open={isOpen("Overview")} toggle={toggleSection}>
        <div className="grid lg:grid-cols-3 gap-6">
          {dups.length > 0 && (
            <div className="lg:col-span-3 flex items-start gap-3 bg-red-50 border border-red-300 rounded-md p-4" data-testid="duplicate-banner">
              <Icons.AlertTriangle size={20} className="text-red-500 mt-0.5 shrink-0" />
              <div className="text-sm w-full">
                <div className="font-semibold text-red-800">Possible duplicate — {dups.length} matching lead{dups.length > 1 ? "s" : ""} found</div>
                <div className="mt-1 space-y-1">
                  {dups.map((d) => (
                    <div key={d.id} className="flex flex-wrap items-center gap-x-2 text-red-800">
                      <button className="font-semibold underline" onClick={() => navigate(`/app/leads/${d.id}`)}>{d.uid} · {d.name}</button>
                      <span className="text-xs">assigned to <b>{d.assigned_to_name}</b></span>
                      <span className="text-xs px-1.5 py-0.5 rounded bg-red-100">matched on {d.match_on.join(", ")}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          {lead.callback_request && (
            <div className="lg:col-span-3 flex items-start gap-3 bg-amber-50 border border-amber-300 rounded-md p-4" data-testid="callback-banner">
              <Icons.PhoneCall size={20} className="text-amber-600 mt-0.5 shrink-0" />
              <div className="text-sm">
                <div className="font-semibold text-amber-900">Callback requested by the customer</div>
                <div className="text-amber-800">
                  Preferred time: <b>{fmtDate(lead.callback_request.preferred_at)}</b>
                  {lead.callback_request.phone && <> · Ph: <b>{lead.callback_request.phone}</b></>}
                </div>
                {lead.callback_request.note && <div className="text-amber-700 mt-0.5">“{lead.callback_request.note}”</div>}
              </div>
            </div>
          )}
          <div className="lg:col-span-2 bg-card border border-border rounded-md p-6 shadow-sm">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[["first_name", "First Name"], ["last_name", "Last Name"], ["phone", "Phone"], ["email", "Email"]].map(([k, l]) => (
                <div key={k}><label className="label-caps">{l}</label>
                  <input value={lead[k] || ""} onChange={(e) => setField(k, e.target.value)}
                    data-testid={`lead-detail-${k}`} className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background" /></div>
              ))}
              <div><label className="label-caps">Alternate Name (optional)</label>
                <input value={lead.alt_contact_name || ""} onChange={(e) => setField("alt_contact_name", e.target.value)}
                  data-testid="lead-detail-alt_contact_name" placeholder="e.g. spouse / co-owner" className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background" /></div>
              <div><label className="label-caps">Alternate Phone (optional)</label>
                <input value={lead.alt_contact_phone || ""} onChange={(e) => setField("alt_contact_phone", e.target.value)}
                  data-testid="lead-detail-alt_contact_phone" placeholder="Alternate contact number" className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background" /></div>
              <div className="sm:col-span-2"><label className="label-caps">Interested Solution</label>
                <select value={lead.interested_solution || ""} onChange={(e) => setField("interested_solution", e.target.value)}
                  data-testid="lead-detail-interested" className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background">
                  <option value="">— Select —</option>{INTERESTED_SOLUTIONS.map((s) => <option key={s}>{s}</option>)}</select></div>
              <div className="sm:col-span-2"><label className="label-caps">Address (Google autocomplete)</label>
                <AddressAutocomplete value={lead.street} testid="lead-detail-street"
                  onManualChange={(v) => setField("street", v)}
                  onSelect={(a) => setLead((l) => ({ ...l, ...a }))} /></div>
              <div><label className="label-caps">Suburb</label>
                <input value={lead.suburb || ""} onChange={(e) => setField("suburb", e.target.value)}
                  data-testid="lead-detail-suburb" className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background" /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><label className="label-caps">State</label>
                  <select value={lead.state} onChange={(e) => setField("state", e.target.value)}
                    data-testid="lead-detail-state" className="w-full mt-1 px-2 py-2 rounded-md border border-input bg-background">
                    {AU_STATES.map((s) => <option key={s}>{s}</option>)}</select></div>
                <div><label className="label-caps">Postcode</label>
                  <input value={lead.postcode || ""} onChange={(e) => setField("postcode", e.target.value)}
                    data-testid="lead-detail-postcode" className="w-full mt-1 px-2 py-2 rounded-md border border-input bg-background" /></div>
              </div>
              <div><label className="label-caps">Priority</label>
                <select value={lead.priority || "Medium"} onChange={(e) => setField("priority", e.target.value)}
                  data-testid="lead-detail-priority" className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background">
                  {LEAD_PRIORITIES.map((p) => <option key={p}>{p}</option>)}</select></div>
              <div><label className="label-caps">Next Follow-up · Melbourne</label>
                <input type="datetime-local" value={melbourneInput(lead.next_follow_up)} onChange={(e) => setField("next_follow_up", e.target.value)}
                  data-testid="lead-detail-followup" className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background" /></div>
              <div className="sm:col-span-2"><label className="label-caps">Notes</label>
                <textarea value={lead.notes || ""} onChange={(e) => setField("notes", e.target.value)} rows={2}
                  className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background" /></div>
            </div>
            <p className="text-xs text-muted-foreground mt-3">Tip: correct the Google-formatted address here for leads captured from Facebook.</p>
            <button onClick={saveLead} data-testid="save-lead" className="mt-4 px-5 py-2 rounded-md bg-primary text-primary-foreground font-semibold">Save Changes</button>
          </div>

          {/* Right column: assignment + source details */}
          <div className="space-y-4">
            <div className="bg-card border border-border rounded-md p-5 shadow-sm" data-testid="lead-assignment-panel">
              <h3 className="font-head font-semibold mb-3 flex items-center gap-2"><Icons.UserCheck size={16} className="text-secondary" /> Assignment</h3>
              <label className="label-caps">Assigned To</label>
              {canAssign ? (
                <select value={lead.assigned_to || ""} onChange={(e) => assign(e.target.value)} data-testid="assign-select"
                  className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background">
                  <option value="">Unassigned</option>{users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}</select>
              ) : (
                <div className="mt-1 font-medium">{userName(lead.assigned_to)}</div>
              )}
              {lead.assigned_at && <p className="text-xs text-muted-foreground mt-2">Assigned {fmtDate(lead.assigned_at)}</p>}
              <label className="flex items-start gap-2 mt-3 pt-3 border-t border-border cursor-pointer text-sm" title="When on, the customer thank-you and 'now with a specialist' emails are not sent for this lead.">
                <input type="checkbox" checked={!!lead.suppress_emails} data-testid="lead-suppress-toggle"
                  onChange={async (e) => { const v = e.target.checked; try { const { data } = await api.put(`/leads/${id}`, { suppress_emails: v }); setLead(data); toast.success(v ? "Automated emails paused for this lead" : "Automated emails enabled"); } catch (err) { toast.error(errMsg(err)); } }}
                  className="mt-0.5" />
                <span className="text-muted-foreground">Pause automated emails for this lead</span>
              </label>
            </div>

            <div className="bg-card border border-border rounded-md p-5 shadow-sm" data-testid="lead-booking-panel">
              <h3 className="font-head font-semibold mb-1 flex items-center gap-2"><Icons.CalendarClock size={16} className="text-secondary" /> Callback booking</h3>
              <p className="text-xs text-muted-foreground mb-3">Share this link so the customer can book a free callback at their preferred time. It also goes out automatically in the thank-you email.</p>
              {bookingLink ? (
                <div className="space-y-2">
                  <input readOnly value={bookingLink} data-testid="booking-link-input" onFocus={(e) => e.target.select()}
                    className="w-full px-3 py-2 rounded-md border border-input bg-muted/40 text-xs" />
                  <div className="flex gap-2">
                    <button data-testid="copy-booking-link" onClick={() => { navigator.clipboard?.writeText(bookingLink); toast.success("Booking link copied"); }}
                      className="flex-1 px-3 py-2 rounded-md bg-primary text-primary-foreground text-sm font-semibold flex items-center justify-center gap-1.5"><Icons.Copy size={14} /> Copy</button>
                    <a href={bookingLink} target="_blank" rel="noreferrer" className="px-3 py-2 rounded-md border border-border text-sm font-medium flex items-center gap-1.5"><Icons.ExternalLink size={14} /> Open</a>
                  </div>
                </div>
              ) : (
                <button data-testid="get-booking-link"
                  onClick={async () => { try { const { data } = await api.get(`/leads/${lead.id}/booking-link`); setBookingLink(data.link); } catch (e) { toast.error(errMsg(e)); } }}
                  className="w-full px-3 py-2 rounded-md border border-primary text-primary text-sm font-semibold flex items-center justify-center gap-1.5"><Icons.Link2 size={15} /> Get booking link</button>
              )}
              {lead.callback_request && (
                <div className="mt-3 text-xs bg-amber-50 border border-amber-200 rounded-md p-2.5 text-amber-800">
                  <b>Requested:</b> {fmtDate(lead.callback_request.preferred_at)}{lead.callback_request.phone ? ` · ${lead.callback_request.phone}` : ""}
                </div>
              )}
            </div>

            <div className="bg-muted/30 border border-border rounded-md p-5 text-sm" data-testid="lead-source-panel">
              <h3 className="font-head font-semibold mb-3 flex items-center gap-2"><Icons.Radio size={16} className="text-secondary" /> Lead Source</h3>
              {[["Source", lead.source], ["Facebook Form", lead.fb_form_name], ["Form ID", lead.fb_form_id],
                ["Facebook Lead ID", lead.fb_leadgen_id], ["Received", lead.received_at ? fmtDate(lead.received_at) : null],
                ["Created", fmtDate(lead.created_at)]].filter(([, v]) => v).map(([k, v]) => (
                <div key={k} className="flex justify-between gap-3 py-1 border-b border-border/40 last:border-0">
                  <span className="text-muted-foreground">{k}</span>
                  <span className="font-medium text-right break-all">{v}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>)}

      {show("Property Details") && (<Section merged={merged} name="Property Details" icon="Home" open={isOpen("Property Details")} toggle={toggleSection}>
        <div className="space-y-6 max-w-3xl">
          <div className="bg-card border border-border rounded-md p-6 shadow-sm">
            <h3 className="font-head font-semibold mb-4">Section 1 — Roof & Structure</h3>
            <div className="grid sm:grid-cols-3 gap-4">
              <Sel label="Property Type" value={prop.property_type} opts={PROPERTY_OPTIONS.property_type} onChange={(v) => setProp({ ...prop, property_type: v })} testid="prop-type" />
              <Sel label="No. of Stories" value={prop.no_of_stories} opts={PROPERTY_OPTIONS.no_of_stories} onChange={(v) => setProp({ ...prop, no_of_stories: v })} testid="prop-stories" />
              <Sel label="Roof Type" value={prop.roof_type} opts={PROPERTY_OPTIONS.roof_type} onChange={(v) => setProp({ ...prop, roof_type: v })} testid="prop-roof-type" />
              <Sel label="Roof Pitch" value={prop.roof_pitch} opts={PROPERTY_OPTIONS.roof_pitch} onChange={(v) => setProp({ ...prop, roof_pitch: v })} testid="prop-roof-pitch" />
              <Sel label="Tilt Requirement" value={prop.tilt_requirement} opts={PROPERTY_OPTIONS.tilt_requirement} onChange={(v) => setProp({ ...prop, tilt_requirement: v })} testid="prop-tilt" />
            </div>
          </div>
          <div className="bg-card border border-border rounded-md p-6 shadow-sm">
            <h3 className="font-head font-semibold mb-4">Section 2 — Electrical</h3>
            <div className="grid sm:grid-cols-3 gap-4">
              <Sel label="Phase" value={prop.phase} opts={PROPERTY_OPTIONS.phase} onChange={(v) => setProp({ ...prop, phase: v })} testid="prop-phase" />
              <Sel label="Switch Board" value={prop.switchboard} opts={PROPERTY_OPTIONS.switchboard} onChange={(v) => setProp({ ...prop, switchboard: v })} testid="prop-switchboard" />
              <Sel label="Internet Connection" value={prop.internet} opts={PROPERTY_OPTIONS.internet} onChange={(v) => setProp({ ...prop, internet: v })} testid="prop-internet" />
              <div><label className="label-caps">Meter No.</label>
                <input value={prop.meter_no || ""} onChange={(e) => setProp({ ...prop, meter_no: e.target.value })} data-testid="prop-meter-no" className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background" /></div>
              <div><label className="label-caps">NMI No.</label>
                <input value={prop.nmi_no || ""} onChange={(e) => setProp({ ...prop, nmi_no: e.target.value })} data-testid="prop-nmi-no" className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background" /></div>
            </div>
          </div>
          <div className="bg-card border border-border rounded-md p-6 shadow-sm">
            <h3 className="font-head font-semibold mb-4">Section 3 — Retailer</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <Sel label="Electric Distributor" value={prop.distributor} opts={PROPERTY_OPTIONS.distributor} onChange={(v) => setProp({ ...prop, distributor: v })} testid="prop-distributor" />
              <div><label className="label-caps">Electric Retailer</label>
                <input value={prop.retailer || ""} onChange={(e) => setProp({ ...prop, retailer: e.target.value })} data-testid="prop-retailer" className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background" /></div>
            </div>
          </div>
          <button onClick={saveProp} data-testid="save-property" className="px-5 py-2 rounded-md bg-primary text-primary-foreground font-semibold">Save Property Details</button>

          {fin && (
            <div className="bg-card border border-border rounded-md p-6 shadow-sm" data-testid="finalization-panel">
              <h3 className="font-head font-semibold mb-1 flex items-center gap-2"><Icons.ClipboardCheck size={18} className="text-primary" /> Sale Finalization</h3>
              <p className="text-sm text-muted-foreground mb-4">After the customer signs, complete all property details, then submit for admin/manager approval.</p>
              <div className="flex flex-wrap gap-2 mb-4">
                <StatusPill ok={fin.quote_signed} label={fin.quote_signed ? "Quote signed" : "Quote not signed"} />
                <StatusPill ok={fin.missing_fields.length === 0} label={fin.missing_fields.length === 0 ? "All property details complete" : `${fin.missing_fields.length} field(s) missing`} />
                <span className={`text-xs font-semibold px-3 py-1 rounded-full ${
                  fin.status === "approved" ? "bg-emerald-100 text-emerald-700" :
                  fin.status === "pending_approval" ? "bg-amber-100 text-amber-700" :
                  fin.status === "rejected" ? "bg-red-100 text-red-700" : "bg-muted text-muted-foreground"}`}>
                  {fin.status === "approved" ? "Finalized ✓" : fin.status === "pending_approval" ? "Pending approval" : fin.status === "rejected" ? "Sent back" : "Not submitted"}
                </span>
              </div>
              {fin.missing_fields.length > 0 && (
                <p className="text-xs text-muted-foreground mb-3">Missing: {fin.missing_fields.join(", ")}</p>
              )}
              {fin.status === "rejected" && fin.reject_reason && (
                <p className="text-sm text-red-600 mb-3 bg-red-50 rounded-md p-3">Sent back: {fin.reject_reason}</p>
              )}
              <div className="flex flex-wrap gap-2">
                {fin.status !== "approved" && fin.status !== "pending_approval" && (
                  <button onClick={submitFin} disabled={!fin.can_submit} data-testid="submit-finalization"
                    className="px-5 py-2 rounded-md bg-secondary text-secondary-foreground font-semibold disabled:opacity-50">Submit for Approval</button>
                )}
                {fin.status === "pending_approval" && fin.can_approve && (
                  <>
                    <button onClick={approveFin} data-testid="approve-finalization" className="px-5 py-2 rounded-md bg-emerald-600 text-white font-semibold">Approve &amp; Finalize</button>
                    <button onClick={rejectFin} data-testid="reject-finalization" className="px-5 py-2 rounded-md border border-red-300 text-red-600 font-semibold">Send Back</button>
                  </>
                )}
                {fin.status === "pending_approval" && !fin.can_approve && (
                  <span className="text-sm text-muted-foreground">Awaiting approval from admin / manager.</span>
                )}
              </div>
            </div>
          )}
        </div>
      </Section>)}

      {show("Documents") && (<Section merged={merged} name="Documents" icon="FileText" open={isOpen("Documents")} toggle={toggleSection}>
        <div className="bg-card border border-border rounded-md p-6 shadow-sm max-w-2xl" data-testid="documents-tab">
          <h3 className="font-head font-semibold mb-4">Documents</h3>

          <div className="border border-dashed border-border rounded-md p-4 mb-6 bg-muted/30" data-testid="doc-upload-panel">
            <div className="text-sm font-medium mb-3">Upload a document</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="label-caps">Label</label>
                <select value={docLabel} onChange={(e) => setDocLabel(e.target.value)} data-testid="doc-label-select"
                  className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background">
                  {DOC_LABELS.map((o) => <option key={o}>{o}</option>)}
                </select>
              </div>
              {docLabel === "Custom" && (
                <div>
                  <label className="label-caps">Custom name</label>
                  <input value={docCustom} onChange={(e) => setDocCustom(e.target.value)} placeholder="e.g. Contract" data-testid="doc-custom-label"
                    className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background" />
                </div>
              )}
              <div className={docLabel === "Custom" ? "sm:col-span-2" : ""}>
                <label className="label-caps">File(s)</label>
                <input type="file" multiple onChange={(e) => setDocFile(Array.from(e.target.files || []))} data-testid="doc-file-input"
                  className="w-full mt-1 text-sm file:mr-3 file:px-3 file:py-1.5 file:rounded-md file:border-0 file:bg-primary file:text-primary-foreground file:text-sm" />
                {docFile.length > 1 && <p className="text-xs text-muted-foreground mt-1" data-testid="doc-file-count">{docFile.length} files selected</p>}
              </div>
            </div>
            <button onClick={uploadDoc} disabled={docUploading} data-testid="doc-upload-btn"
              className="mt-4 flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground font-semibold text-sm disabled:opacity-60">
              <Icons.Upload size={16} /> {docUploading ? "Uploading…" : docFile.length > 1 ? `Upload ${docFile.length} files` : "Upload"}
            </button>
          </div>

          {docs.length === 0 && <p className="text-sm text-muted-foreground">No documents yet. Upload above, or documents uploaded by the customer via the quote link will appear here.</p>}
          <div className="space-y-2">
          <SignedProposalArchive leadId={id} onSaved={reloadDocs} />
          <ProposalPdfArchive leadId={id} onSaved={reloadDocs} />
          {docs.map((d) => (
              <div key={d.id} className="flex items-center gap-3 p-3 rounded-md border border-border" data-testid={`doc-row-${d.id}`}>
                <Icons.FileText size={18} className="text-secondary shrink-0" />
                {editingDoc === d.id ? (
                  <div className="flex-1 flex flex-wrap items-center gap-2">
                    <select value={editLabel} onChange={(e) => setEditLabel(e.target.value)} data-testid="doc-edit-label-select"
                      className="px-2 py-1.5 rounded-md border border-input bg-background text-sm">
                      {DOC_LABELS.map((o) => <option key={o}>{o}</option>)}
                    </select>
                    {editLabel === "Custom" && (
                      <input value={editCustom} onChange={(e) => setEditCustom(e.target.value)} placeholder="Custom name" data-testid="doc-edit-custom"
                        className="px-2 py-1.5 rounded-md border border-input bg-background text-sm" />
                    )}
                    <button onClick={() => saveEditDoc(d.id)} data-testid="doc-edit-save" className="text-emerald-600 text-sm font-semibold">Save</button>
                    <button onClick={() => setEditingDoc(null)} data-testid={`doc-edit-cancel-${d.id}`} className="text-muted-foreground text-sm">Cancel</button>
                  </div>
                ) : (
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate" title={d.label || d.filename} data-testid={`doc-label-${d.id}`}>{d.label || d.filename}</div>
                    <div className="text-xs text-muted-foreground truncate" data-testid={`doc-info-${d.id}`}>{d.label ? `${d.filename} · ` : ""}{fmtDate(d.created_at)} · {d.uploaded_by}</div>
                  </div>
                )}
                <button type="button" onClick={() => openFile(d.file_id).catch((e) => toast.error(errMsg(e, "Could not open document")))} className="text-primary text-sm underline shrink-0" data-testid={`doc-view-${d.id}`}>View</button>
                <button type="button" onClick={() => setShareDocument(d.id)} title="Share in chat" aria-label="Share in chat" data-testid={`doc-share-${d.id}`} className="text-secondary shrink-0 p-1 hover:bg-muted rounded"><Icons.Share2 size={16} /></button>
                {d.kind === "proposal" && <button type="button" onClick={() => openFile(d.file_id, { download: true }).catch((e) => toast.error(errMsg(e, "Could not download proposal")))} title="Download proposal PDF" aria-label="Download proposal PDF"
                  className="text-primary shrink-0 hover:text-primary/80 transition-colors" data-testid={`doc-download-${d.id}`}><Icons.Download size={16} /></button>}
                {editingDoc !== d.id && (
                  <button onClick={() => startEditDoc(d)} data-testid={`doc-relabel-${d.id}`} className="text-secondary shrink-0" title="Rename / relabel">
                    <Icons.Pencil size={15} />
                  </button>
                )}
                {canDeleteDoc && (
                  <button onClick={() => setConfirm({ title: "Delete document?", description: "This document will be removed from the lead.", label: "Delete document", run: () => deleteDoc(d.id) })} data-testid={`doc-delete-${d.id}`} className="text-red-500 shrink-0" title="Delete">
                    <Icons.Trash2 size={15} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </Section>)}

      {show("Chat") && <Section merged={merged} name="Chat" icon="MessagesSquare" open={isOpen("Chat")} toggle={toggleSection}><LeadChat lead={lead} /></Section>}
      {shareDocument && <ShareLeadDialog lead={lead} documentId={shareDocument} onClose={() => setShareDocument(null)} />}
      {show("Prepost") && <Section merged={merged} name="Prepost" icon="ClipboardCheck" open={isOpen("Prepost")} toggle={toggleSection}><PrepostPanel lead={lead} quotes={quotes} onRefresh={load} onOpenChat={() => { setTab("Chat"); setSearchParams({ tab: "Chat" }); setCollapsed((v) => ({ ...v, Chat: false })); setTimeout(() => document.querySelector('[data-testid="lead-chat"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 100); }} /></Section>}

      {show("Tax Invoice") && <Section merged={merged} name="Tax Invoice" icon="Receipt" open={isOpen("Tax Invoice")} toggle={toggleSection}><TaxInvoicePanel leadId={id} lead={lead} /></Section>}
      </div>
      <div className={merged ? "space-y-4" : "contents"}>
      {show("Activity") && (<Section merged={merged} name="Activity" icon="Activity" open={isOpen("Activity")} toggle={toggleSection}>
        <ActivityHub lead={lead} activities={activities} comms={comms} commsCfg={commsCfg} calling={calling}
          onRefresh={() => { loadAct(); loadComms(); }} onCall={startCall} />
      </Section>)}

      {show("Quotes") && (<Section merged={merged} name="Quotes" icon="FileSignature" open={isOpen("Quotes")} toggle={toggleSection}>
        <div className="space-y-4">
          {canEdit && <button onClick={() => navigate(`/app/quotes/new?lead=${id}`)} data-testid="lead-new-quote"
            className="flex items-center gap-2 px-4 py-2.5 rounded-md bg-primary text-primary-foreground font-semibold">
            <Icons.Plus size={18} /> Create Quote for this Lead</button>}
          <div className="space-y-2">
            {quotes.map((q) => (
              <div key={q.id} data-testid={`quote-row-${q.quote_no}`} className="flex items-center gap-3 bg-card border border-border rounded-md p-4">
                <div className="flex-1 min-w-0 break-words">
                  <div className="font-semibold text-primary">{q.quote_no}</div>
                  <div className="text-xs text-muted-foreground">{QUOTE_TYPES.find((x) => x.key === q.quote_type)?.label} · {currency(q.out_of_pocket)} out of pocket · {q.status}{q.signed && " ✓ signed"}</div>
                </div>
                <QuotePdfActions quote={q} id={`lead-quote-actions-${q.id}`} compact label={`Actions for ${q.quote_no}`} items={[
                  { id: `quote-edit-${q.quote_no}`, icon: "Pencil", label: q.signed ? "Edit (admin)" : "Edit quote", hidden: q.signed && !isSuper, onSelect: () => navigate(`/app/quotes/${q.id}`) },
                  { id: `quote-open-${q.quote_no}`, icon: "Eye", label: "Preview Quote", onSelect: () => window.open(`/quote/${q.public_token}?preview=1`, "_blank", "noopener,noreferrer") },
                  { id: `quote-duplicate-${q.quote_no}`, icon: "Copy", label: "Duplicate quote", onSelect: () => duplicateQuote(q) },
                  ...(["super_admin", "admin", "admin_manager"].includes(user?.role) ? [{ id: `lead-delete-quote-${q.id}`, icon: "Trash2", label: "Move to recycle bin", onSelect: async () => {
                    if (!window.confirm(`Move ${q.quote_no} to the recycle bin? Public access will be disabled. Payments and invoices are retained.`)) return;
                    try { await api.delete(`/quotes/${q.id}`); toast.success("Quote moved to recycle bin"); load(); } catch (e) { toast.error(errMsg(e)); }
                  } }] : []),
                ]} />
              </div>
            ))}
            {quotes.length === 0 && <p className="text-sm text-muted-foreground">No quotes for this lead yet.</p>}
          </div>
        </div>
      </Section>)}
      </div>
      </div>

      <ConfirmAction action={confirm} onClose={() => setConfirm(null)} />
      {cbShow && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setCbShow(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={createCallback} data-testid="callback-dialog" className="bg-card rounded-md w-full max-w-md p-6 shadow-lg">
            <h2 className="text-xl font-head font-extrabold mb-4">Schedule Callback</h2>
            <label className="label-caps">Title</label>
            <input value={cb.title} onChange={(e) => setCb({ ...cb, title: e.target.value })} data-testid="callback-title" className="w-full mt-1 mb-3 px-3 py-2 rounded-md border border-input bg-background" />
            <label className="label-caps">Call back at</label>
            <input required type="datetime-local" value={cb.due_at} onChange={(e) => setCb({ ...cb, due_at: e.target.value })}
              data-testid="callback-time" className="w-full min-w-0 mt-1 mb-2 px-3 py-2 rounded-md border border-input bg-background" />
            <p className="text-xs text-muted-foreground mb-4" data-testid="callback-timezone">Australia/Melbourne · Alerts 10 minutes before and at the scheduled time</p>
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={() => setCbShow(false)} data-testid="callback-cancel" className="px-4 py-2 rounded-md border border-border">Cancel</button>
              <button type="submit" data-testid="callback-submit" className="px-4 py-2 rounded-md bg-primary text-primary-foreground font-semibold">Add to Calendar</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
